import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router';
import {
  LayoutDashboard,
  Compass,
  Package,
  BarChart3,
  Zap,
  MessageSquare,
  Bot,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Receipt,
  FileText,
  Tag,
  Map,
  Layers,
  Gem,
} from 'lucide-react';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { size?: number | string }>;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/inventory', label: 'Inventory', icon: Package },
  { to: '/messages', label: 'Messages', icon: MessageSquare },
  { to: '/offers', label: 'Offers', icon: Tag },
  { to: '/listings/queued', label: 'Queued', icon: Layers },
  { to: '/transactions', label: 'Transactions', icon: Receipt },
  { to: '/tax-export', label: 'Tax Export', icon: FileText },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/automation', label: 'Automation', icon: Zap },
  { to: '/agents/activity', label: 'Agents', icon: Bot },
  { to: '/sourcing', label: 'Sourcing', icon: Compass },
  { to: '/valuation', label: 'Valuation', icon: Gem },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/roadmap', label: 'Roadmap', icon: Map },
];

export interface FosSidebarProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export function FosSidebar({ collapsed, onToggleCollapsed }: FosSidebarProps) {
  const { pathname } = useLocation();

  const isActive = (to: string) => {
    if (to === '/') return pathname === '/';
    return pathname === to || pathname.startsWith(`${to}/`);
  };

  return (
    <aside
      className={cn(
        'flex flex-col h-dvh flex-shrink-0',
        'bg-fos-bg-surface border-r border-fos-border',
        'transition-all duration-fos-slow',
        collapsed ? 'w-[60px]' : 'w-[220px]',
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          'flex items-center h-14 border-b border-fos-border',
          collapsed ? 'justify-center px-2' : 'justify-between px-4',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-fos bg-fos-accent">
            <Sparkles size={14} className="text-fos-text-inverse" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <div className="flex min-w-0 flex-col">
              <span className="text-[13px] font-semibold leading-tight text-fos-text-primary">
                For Visibility
              </span>
              <span className="text-[10px] uppercase leading-tight tracking-[0.08em] text-fos-text-muted">
                Reseller OS
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Nav items */}
      <nav className="fos-scrollbar flex-1 overflow-y-auto py-3" data-tour-target="sidebar-nav">
        <ul className="flex flex-col gap-0.5 px-2">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  title={collapsed ? item.label : undefined}
                  data-tour-target={`sidebar-${item.label.toLowerCase()}`}
                  className={cn(
                    'flex items-center h-9 rounded-fos transition-colors duration-fos',
                    'text-[13px] font-medium',
                    collapsed ? 'justify-center px-0' : 'gap-3 px-3',
                    active
                      ? 'bg-fos-accent-bg text-fos-accent'
                      : 'text-fos-text-secondary hover:text-fos-text-primary hover:bg-fos-bg-elevated',
                  )}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Collapse toggle */}
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={cn(
          'flex items-center h-10 border-t border-fos-border',
          'text-fos-text-muted hover:text-fos-text-primary hover:bg-fos-bg-elevated',
          'transition-colors duration-fos',
          collapsed ? 'justify-center' : 'justify-end px-4 gap-2 text-xs',
        )}
      >
        {collapsed ? (
          <ChevronRight size={14} />
        ) : (
          <>
            <span>Collapse</span>
            <ChevronLeft size={14} />
          </>
        )}
      </button>
    </aside>
  );
}
